import React, { useEffect, useState } from "react";
import axios from "axios";
import { Camera, Upload, X, ImageOff } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * Order Information / National Reference — simplified middle block.
 *
 * Title + up to 2 reference photo slots. Nothing else (per Apr 22 2026 spec).
 * These are NOT the inspector's completion photos — those stay in the
 * separate NationalPhotosPanel below.
 *
 * Office roles can upload / delete. Inspectors view-only.
 */
const OrderReferenceBlock = ({ orderId, userRole }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [err, setErr] = useState("");

  const MAX = 2;
  const canManage = ["Admin", "Finance", "Scheduling", "Dispatch", "Operations", "Operator"].includes(userRole);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/${orderId}/description-photos`);
      setPhotos(res.data || []);
    } catch (e) {
      console.error("reference photos load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
    // eslint-disable-next-line
  }, [orderId]);

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX - photos.length;
    if (files.length > remaining) {
      setErr(`Only ${remaining} more photo${remaining === 1 ? "" : "s"} can be uploaded (max ${MAX}).`);
      return;
    }
    setErr("");
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/orders/${orderId}/description-photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await fetchPhotos();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (p) => {
    if (!window.confirm("Delete this reference photo?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/description-photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete");
    }
  };

  // 2026-06-02 (Angela hard-lock fix) — pick the URL that survives prod
  // deploys. New uploads carry `gridfs_id`+`persistent_url` (GridFS, persistent)
  // and the legacy `file_url` (local disk, wiped on every deploy). Same
  // resolver pattern used by NationalPhotosPanel.jsx:20-25.
  const photoUrl = (p) => {
    if (!p) return "";
    const BASE = process.env.REACT_APP_BACKEND_URL || "";
    if (p.persistent_url) return `${BASE}${p.persistent_url}`;
    if (p.gridfs_id)      return `${BASE}/api/files/description_photos/${p.gridfs_id}`;
    return `${BASE}${p.file_url || ""}`;
  };

  // Render fixed 2 slots. Each slot is either a photo (tap to zoom, X to
  // delete) or an empty upload tile (office only).
  const slots = [0, 1].map((idx) => photos[idx] || null);

  return (
    <div
      className="bg-[#FDFBF4] rounded-lg border border-[#E8D9B5] p-4 mb-6"
      data-testid="order-reference-block"
    >
      <div className="flex items-center gap-2 mb-3">
        <Camera className="h-4 w-4 text-[#9A7735]" />
        <h2 className="text-sm font-semibold text-[#1F2937] uppercase tracking-wide">
          Order Information / National Reference
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="reference-photos-grid">
        {slots.map((p, i) =>
          p ? (
            <div key={p.id} className="relative group">
              <button
                onClick={() => setLightbox(p)}
                className="block w-full aspect-square bg-white rounded-md overflow-hidden border border-[#E8D9B5] hover:ring-2 hover:ring-[#9A7735]/50 transition-all"
                data-testid={`reference-photo-${i}`}
              >
                <img
                  src={photoUrl(p)}
                  alt={p.file_name}
                  className="w-full h-full object-cover"
                />
              </button>
              {canManage && (
                <button
                  onClick={() => deletePhoto(p)}
                  className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-[#F7DADA] text-gray-600 hover:text-[#8B4545] rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove photo"
                  data-testid={`reference-photo-delete-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ) : canManage ? (
            <label
              key={`empty-${i}`}
              className="aspect-square flex flex-col items-center justify-center gap-1 bg-white/60 border border-dashed border-[#E8D9B5] rounded-md cursor-pointer hover:bg-white hover:border-[#9A7735]/60 transition-colors text-[#9A7735]"
              data-testid={`reference-photo-upload-slot-${i}`}
            >
              <Upload className="h-5 w-5" />
              <span className="text-[11px] font-semibold">
                {uploading ? "Uploading…" : `Add Photo ${i + 1}`}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onFilesSelected}
                disabled={uploading}
                className="hidden"
                data-testid={`reference-photo-input-${i}`}
              />
            </label>
          ) : (
            <div
              key={`placeholder-${i}`}
              className="aspect-square flex flex-col items-center justify-center gap-1 bg-white/40 border border-dashed border-[#E8D9B5] rounded-md text-gray-400"
              data-testid={`reference-photo-empty-${i}`}
            >
              <ImageOff className="h-5 w-5" />
              <span className="text-[10px]">No photo</span>
            </div>
          )
        )}
      </div>

      {err && <p className="mt-2 text-[11px] text-[#C07070]">{err}</p>}
      {loading && photos.length === 0 && (
        <p className="mt-2 text-[10px] text-gray-400 italic">Loading…</p>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setLightbox(null)}
          data-testid="reference-photo-lightbox"
        >
          <img
            src={photoUrl(lightbox)}
            alt={lightbox.file_name}
            className="max-h-[92vh] max-w-[95vw] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/25 text-white rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderReferenceBlock;
